import { useEffect, useState } from "react";
import { Landmark, Trophy, Timer, AlertTriangle } from "lucide-react";
import { getAnalytics } from "./api/CrmApi";
import type { CrmAnalytics } from "./api/CrmApi";
import { CrmProvider, useCrm } from "./modal/CrmContext";
import { PipelineNotSetUp } from "./ui/crmUi";
import PageHeader from "shared/PageHeader";
import KpiCard from "shared/KpiCard";

// Series colors (dataviz six checks): received = brand, won = success. Both
// resolve through the theme variables so the chart recolors with the theme
// and dark mode. Won bars carry direct labels as the required secondary
// encoding. Text always wears text tokens, never series color.
const RECEIVED = "hsl(var(--primary))";
const WON = "hsl(var(--success))";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface">
      <h2 className="text-sm font-semibold text-foreground px-4 py-3 border-b border-border">{title}</h2>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** Labeled horizontal bar row — doubles as the table view (label + exact value on every row). */
function BarRow({ label, value, max, suffix, muted }: { label: string; value: number; max: number; suffix?: string; muted?: string }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1" title={`${label}: ${value}${suffix ?? ""}`}>
      <span className="w-40 shrink-0 text-xs text-muted-foreground truncate">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-surface-muted overflow-hidden">
        <div
          className="h-2.5 rounded-full hover:opacity-80 transition-opacity"
          style={{ width: `${pct}%`, background: RECEIVED }}
        />
      </div>
      <span className="w-20 shrink-0 text-right text-xs text-foreground">
        {value}{suffix ?? ""}
        {muted && <span className="text-muted-foreground"> {muted}</span>}
      </span>
    </div>
  );
}

function CrmAnalyticsContent() {
  const { pipeline, pipelineLoaded } = useCrm();
  const [data, setData] = useState<CrmAnalytics | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pipeline) return;
    setData(null);
    setError("");
    getAnalytics(pipeline.key)
      .then(setData)
      .catch((e) => setError(e.response?.data?.detail || "Failed to load analytics"));
  }, [pipeline]);

  if (pipelineLoaded && !pipeline) {
    return <PipelineNotSetUp />;
  }

  return (
    <div className="flex flex-col gap-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!error && !data && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {data && <CrmAnalyticsBody data={data} />}
    </div>
  );
}

export default function CrmAnalyticsPage() {
  return (
    <CrmProvider>
      <CrmAnalyticsContent />
    </CrmProvider>
  );
}

function CrmAnalyticsBody({ data }: { data: CrmAnalytics }) {
  const dp = data.deadline_performance;
  const maxMonth = Math.max(1, ...data.volume_by_month.flatMap((m) => [m.received, m.won]));
  const maxReached = Math.max(1, ...data.funnel.map((f) => f.reached));
  const maxDays = Math.max(1, ...data.time_in_stage.map((t) => t.avg_days ?? 0));
  const maxAgency = Math.max(1, ...data.top_agencies.map((a) => a.count));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="SLED CRM Analytics"
        description="Computed from the pipeline's full activity history. Backfilled records carry their archive month but no stage timings."
      />

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard tone="brand" icon={Landmark} label="Total RFPs" value={String(data.total_opportunities)} />
        <KpiCard
          tone="success"
          icon={Trophy}
          label="Win rate"
          value={data.win_rate_pct !== null ? `${data.win_rate_pct}%` : "—"}
          hint="closed_won share of decided"
        />
        <KpiCard
          tone="info"
          icon={Timer}
          label="Avg outreach runway"
          value={dp.avg_runway_days !== null ? `${dp.avg_runway_days}d` : "—"}
          hint={`${dp.campaigns_measured} campaign${dp.campaigns_measured === 1 ? "" : "s"} measured`}
        />
        <KpiCard
          tone="danger"
          icon={AlertTriangle}
          label="Launched past deadline"
          value={String(dp.launched_after_deadline)}
          hint="outreach after proposal due"
        />
      </div>

      {/* Volume by month — the one two-series chart (legend + direct labels) */}
      <Section title="RFP Volume by Month">
        <div className="flex items-center gap-4 mb-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: RECEIVED }} /> Received
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: WON }} /> Won
          </span>
        </div>
        <div className="flex items-end gap-4 h-40 overflow-x-auto pb-1">
          {data.volume_by_month.map((m) => (
            <div key={m.month} className="flex flex-col items-center gap-1 shrink-0" title={`${m.month}: ${m.received} received, ${m.won} won`}>
              <div className="flex items-end gap-0.5 h-28">
                <div className="flex flex-col items-center justify-end h-full">
                  <span className="text-[10px] text-foreground mb-0.5">{m.received}</span>
                  <div
                    className="w-6 hover:opacity-80 transition-opacity"
                    style={{ height: `${Math.max(3, (m.received / maxMonth) * 100)}%`, background: RECEIVED, borderRadius: "4px 4px 0 0" }}
                  />
                </div>
                <div className="flex flex-col items-center justify-end h-full">
                  <span className="text-[10px] text-foreground mb-0.5">{m.won}</span>
                  <div
                    className="w-6 hover:opacity-80 transition-opacity"
                    style={{ height: `${Math.max(3, (m.won / maxMonth) * 100)}%`, background: WON, borderRadius: "4px 4px 0 0" }}
                  />
                </div>
              </div>
              <span className="text-[11px] text-muted-foreground">{m.month}</span>
            </div>
          ))}
          {data.volume_by_month.length === 0 && (
            <p className="text-xs text-muted-foreground">No data yet.</p>
          )}
        </div>
      </Section>

      {/* Funnel */}
      <Section title="Pipeline Funnel — RFPs that ever reached each stage">
        {data.funnel.map((f) => (
          <BarRow
            key={f.stage_key}
            label={f.stage_name}
            value={f.reached}
            max={maxReached}
            muted={f.current > 0 ? `(${f.current} now)` : undefined}
          />
        ))}
      </Section>

      {/* Time in stage */}
      <Section title="Average Time in Stage (open stages, real dwell only)">
        {data.time_in_stage.map((t) => (
          <BarRow
            key={t.stage_key}
            label={t.stage_name}
            value={t.avg_days ?? 0}
            max={maxDays}
            suffix="d"
            muted={t.samples > 0 ? `(${t.samples})` : "(no data)"}
          />
        ))}
      </Section>

      {/* Top agencies */}
      <Section title="Top Agencies">
        {data.top_agencies.length === 0 && (
          <p className="text-xs text-muted-foreground">No agency data yet — backfilled records often lack an agency name.</p>
        )}
        {data.top_agencies.map((a) => (
          <BarRow key={a.agency} label={a.agency} value={a.count} max={maxAgency} />
        ))}
      </Section>
    </div>
  );
}
